import { OpenedPhotos } from '@/components/modal-answer-picked/modal-answer-picked-controller.types';
import { ReviewPhotoEntity } from '@/entities/review.entity';
import { useCallback, useEffect, useState } from 'react';

export const useModalAnswerPickedPhotoPreview = () => {
    const [openedPhotos, setOpenedPhotos] = useState<OpenedPhotos | null>(null);
    const [isPhotoLoading, setIsPhotoLoading] = useState<boolean>(false);

    const onPhotoPreviewOpen = (photos: ReviewPhotoEntity[], index: number) => {
        if (!photos.length) {
            return;
        }

        setIsPhotoLoading(true);
        setOpenedPhotos({ photos, index });
    };

    const onPhotoPreviewClose = useCallback(() => {
        setIsPhotoLoading(false);
        setOpenedPhotos(null);
    }, []);

    const onPreviousPhoto = useCallback(() => {
        setOpenedPhotos((prevState) => {
            if (!prevState || prevState.photos.length < 2) {
                return prevState;
            }

            setIsPhotoLoading(true);
            const nextIndex = prevState.index === 0 ? prevState.photos.length - 1 : prevState.index - 1;

            return { ...prevState, index: nextIndex };
        });
    }, []);

    const onNextPhoto = useCallback(() => {
        setOpenedPhotos((prevState) => {
            if (!prevState || prevState.photos.length < 2) {
                return prevState;
            }

            setIsPhotoLoading(true);
            const nextIndex = prevState.index === prevState.photos.length - 1 ? 0 : prevState.index + 1;

            return { ...prevState, index: nextIndex };
        });
    }, []);

    const isPhotoPreviewOpen = !!openedPhotos;

    useEffect(() => {
        if (!isPhotoPreviewOpen) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onPhotoPreviewClose();
                return;
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                onPreviousPhoto();
                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                onNextPhoto();
            }
        };

        // Capture phase makes keyboard navigation resilient when focused controls stop bubbling.
        window.addEventListener('keydown', onKeyDown, true);
        return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [isPhotoPreviewOpen, onNextPhoto, onPhotoPreviewClose, onPreviousPhoto]);

    return {
        openedPhotos,
        openedPhoto: openedPhotos?.photos[openedPhotos.index],
        isPhotoLoading,
        onPhotoPreviewOpen,
        onPhotoPreviewClose,
        onPreviousPhoto,
        onNextPhoto,
        onPhotoPreviewImageLoad: () => setIsPhotoLoading(false),
        onPhotoPreviewImageError: () => setIsPhotoLoading(false),
    };
};
